import { ChipLabel } from '@rhg/components/chip-label';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import {
    Box,
    SxProps,
    LinearProgress,
    Theme,
    Typography,
    useTheme,
    TypographyProps,
} from '@rhg/libs/material';
import { styleMaxLines, sxAppend } from '@rhg/libs/material/sx';
import { ReactNode, useMemo } from 'react';
import { CardVariant } from '../types';

const styleContentContainer = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    position: 'relative',
};
const styleBannerContainerPadding = { px: 6, py: 4 };
const styleContainerPadding = { p: 4 };

const styleCategoryContainer = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
};
const styleCategoryIcon = {
    display: 'flex',
    alignItems: 'center',
    pr: 2,
};
/** This is a hack. In order to achieve perfect vertical alignment of text and image,
 * we set the text's line height equal to the image height. Since this text is always
 * a caption (i.e. fontSize: 12px), we set lineHeight to 4/3 of that (i.e. 16px).
 */
const styleCategoryTextWithIcon = { lineHeight: 'calc(4/3)' };

const styleTitleContainer = (topSpacing: number) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'start',
    mt: topSpacing,
});
const styleTitleText = {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
};
const styleTitleIcon = {
    pr: 4,
};
const styleProgress = { my: 2 };
const styleTagContainer = { mb: 4 };
const styleActionContainer = { position: 'absolute', top: 0, right: 0 };
const styleButtonContainer = (spacing: number) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    mt: spacing,
});
const styleHorizontalContainer = (notInProgressClinical: boolean) => ({
    maxHeight: 116,
    ...(notInProgressClinical
        ? { px: 4, py: 1.5, justifyContent: 'start' }
        : {}),
});
const styleTitle = (titleBottomSpacing: number) => ({
    mb: titleBottomSpacing,
});

interface CardContentProps {
    title: string;
    titleComponent?: React.ElementType;
    titleMaxLines?: number;
    titleTopSpacing?: number;
    titleBottomSpacing?: number;
    subtitle?: string;
    subtitleVariant?: TypographyProps['variant'];
    subtitleMaxLines?: number;
    tag?: string;
    icon?: ReactNode;
    categoryTitle?: string;
    categoryTitleColor?: string;
    categoryIcon?: ReactNode;
    categoryDescription?: string;
    progress?: number;
    progressText?: string;
    progressTextAlt?: string;
    buttonGroup?: ReactNode;
    action?: ReactNode;
    variant?: CardVariant;
    sx?: SxProps<Theme>;
    buttonGroupSx?: SxProps<Theme>;
    buttonTopSpacing?: number;
    additionalAction?: ReactNode;
    skipHorizontalContentAdjustments?: boolean;
}
export default function CardContent({
    title,
    titleComponent = 'div',
    subtitle,
    subtitleVariant = 'caption',
    subtitleMaxLines = 1,
    titleMaxLines,
    titleTopSpacing,
    titleBottomSpacing,
    icon,
    tag,
    categoryTitle,
    categoryTitleColor,
    categoryDescription,
    categoryIcon,
    progress,
    progressText,
    progressTextAlt,
    buttonGroup,
    buttonGroupSx,
    action,
    variant,
    sx,
    buttonTopSpacing = 4,
    additionalAction,
    skipHorizontalContentAdjustments,
}: CardContentProps) {
    const isBanner = variant === 'banner' || variant === 'bannerV2';
    const theme = useTheme();
    const isMobile = useIsMobile();
    const prefersDarkMode = useMemo(
        () => theme.palette.mode === 'dark',
        [theme.palette.mode]
    );

    const titleStyle = useMemo(
        () => ({
            ...styleTitle(
                titleBottomSpacing ?? (typeof progress === 'number' ? 0 : 1)
            ),
            ...styleMaxLines(titleMaxLines),
        }),
        [progress, titleBottomSpacing, titleMaxLines]
    );

    const subtitleStyle = useMemo(
        () => styleMaxLines(subtitleMaxLines),
        [subtitleMaxLines]
    );

    const buttonWrapperStyle = useMemo(
        () => styleButtonContainer(buttonTopSpacing),
        [buttonTopSpacing]
    );

    const titleContainerStyle = useMemo(
        () => styleTitleContainer(titleTopSpacing ?? (isBanner ? 4 : 2)),
        [isBanner, titleTopSpacing]
    );

    const colorOfCategoryTitle = useMemo(
        () =>
            categoryTitleColor ??
            (prefersDarkMode ? 'info.light' : 'info.dark'),
        [categoryTitleColor, prefersDarkMode]
    );

    return (
        <Box
            sx={sxAppend(
                styleContentContainer,
                isBanner ? styleBannerContainerPadding : styleContainerPadding,
                variant === 'horizontal' &&
                    styleHorizontalContainer(
                        !!(
                            !progressText &&
                            categoryIcon &&
                            !skipHorizontalContentAdjustments
                        )
                    ),
                sx
            )}
        >
            {action && <Box sx={styleActionContainer}>{action}</Box>}
            {tag && (
                <Box sx={styleTagContainer}>
                    <ChipLabel variant="transparent" label={tag} />
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={styleCategoryContainer}>
                    {categoryIcon && (
                        <Box sx={styleCategoryIcon}>{categoryIcon}</Box>
                    )}
                    {categoryTitle && (
                        <Typography
                            variant="caption"
                            component="span"
                            color={colorOfCategoryTitle}
                            sx={sxAppend(
                                styleMaxLines(1),
                                !!categoryIcon && styleCategoryTextWithIcon
                            )}
                        >
                            {categoryTitle}
                        </Typography>
                    )}
                    {categoryTitle && categoryDescription && (
                        <Typography
                            variant="caption"
                            component="span"
                            color="text.sub"
                            sx={
                                categoryIcon
                                    ? styleCategoryTextWithIcon
                                    : undefined
                            }
                        >
                            {'\u00A0•\u00A0'}
                        </Typography>
                    )}
                    {categoryDescription && (
                        <Typography
                            variant="caption"
                            component="span"
                            color="text.sub"
                            sx={sxAppend(
                                styleMaxLines(1),
                                !!categoryIcon && styleCategoryTextWithIcon
                            )}
                        >
                            {categoryDescription}
                        </Typography>
                    )}
                </Box>
                {!isBanner && additionalAction}
            </Box>
            <Box sx={titleContainerStyle}>
                {icon && <Box sx={styleTitleIcon}>{icon}</Box>}
                <Box sx={styleTitleText}>
                    <Typography
                        variant={isBanner && !isMobile ? 'h5' : 'body1Demi'}
                        color="text.body"
                        component={titleComponent}
                        title={title}
                        sx={titleStyle}
                    >
                        {title}
                    </Typography>
                    {typeof progress === 'number' &&
                    (progressTextAlt || progressText) ? (
                        <LinearProgress
                            color="primary"
                            variant="determinate"
                            aria-label={progressTextAlt ?? progressText}
                            sx={styleProgress}
                            value={progress * 100}
                            withCurvedCorners
                        />
                    ) : null}
                    {subtitle && (
                        <Typography
                            sx={subtitleStyle}
                            variant={subtitleVariant}
                            color="text.sub"
                            title={subtitle}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
            {(isBanner || variant === 'bannerSquare') &&
                (buttonGroup || additionalAction) && (
                    <Box sx={sxAppend(buttonWrapperStyle, buttonGroupSx)}>
                        {buttonGroup}
                        {additionalAction}
                    </Box>
                )}
        </Box>
    );
}
